import axiosInstance from "./axioInstance";
import { UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";

export const PATHS = Object.freeze({
  GET_MEETINGS_SUMMARY: "/meeting/admin-summary-analysis",
  GET_MEETINGS_TRANSCRIPT_REANALYSIS: "/meeting/collateral/view",
  POST_ACCEPT_MEETING_REANALYSIS: (meeting_id?: string) =>
    meeting_id
      ? `meeting/accept-regenerate-ai-analysis?meeting_id=${meeting_id}`
      : `/meeting/accept-regenerate-ai-analysis`,
  POST_MEETING_REANALYSIS: (meeting_id?: string) =>
    meeting_id
      ? `/meeting/regenerate-ai-analysis?meeting_id=${meeting_id}`
      : `/meeting/regenerate-ai-analysis`,
});

interface GetMeetingsSummaryParams {
  start_time: string;
  end_time: string;
}
interface GetMeetingsTranscriptReanalysisParams {
  meeting_id: string;
  collateral_type: string;
  collateral_id: string;
}
interface PostMeetingReanalysisParams {
  meeting_id: string;
  regenerate: string;
}
interface PostAcceptMeetingReanalysisParams {
  meeting_id: string;
  regenerated_update: string;
}

const getMeetingSummary = async (
  params: GetMeetingsSummaryParams
): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_MEETINGS_SUMMARY}`, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
    },
  });
  return response;
};

const useGetMeetingSummary = (
  params: GetMeetingsSummaryParams,
  enabled: boolean
) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<string>({
    queryKey: ["get_meeting_summary", stringifiedParams],
    queryFn: () => getMeetingSummary(params),
    enabled: enabled,
  });
};

const getMeetingTranscriptReanalysis = async (
  params: GetMeetingsTranscriptReanalysisParams
): Promise<any> => {
  const response = await axiosInstance.get(
    `${PATHS.GET_MEETINGS_TRANSCRIPT_REANALYSIS}`,
    {
      params: {
        meeting_id: params.meeting_id,
        collateral_type: params.collateral_type,
        collateral_id: params.collateral_id,
      },
    }
  );
  return response;
};

const useGetMeetingTranscriptReanalysis = (
  params: GetMeetingsTranscriptReanalysisParams,
  enabled: boolean
) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<string>({
    queryKey: ["get_meeting_transcript_reanalyis", stringifiedParams],
    queryFn: () => getMeetingTranscriptReanalysis(params),
    enabled: enabled,
  });
};

const postAcceptMeetingReanalysis = async (
  meeting_id: string,
  params: PostAcceptMeetingReanalysisParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_ACCEPT_MEETING_REANALYSIS(meeting_id),
    params
  );
  return response;
};

const usePostAcceptMeetingReanalysis = (
  meeting_id: string,
  handlerFunctions: any
) => {
  return useMutation<any, any, PostAcceptMeetingReanalysisParams>({
    mutationKey: ["post_meeting_reanalysis", meeting_id],
    mutationFn: (params) =>
      postAcceptMeetingReanalysis(meeting_id, params).then((res) => res),
    ...handlerFunctions,
  });
};

const postMeetingReanalysis = async (
  meeting_id: string,
  params: PostMeetingReanalysisParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_MEETING_REANALYSIS(meeting_id),
    params
  );
  return response;
};

const usePostMeetingReanalysis = (
  meeting_id: string,
  handlerFunctions: any
) => {
  return useMutation<any, any, PostMeetingReanalysisParams>({
    mutationKey: ["post_meeting_reanalysis", meeting_id],
    mutationFn: (params) =>
      postMeetingReanalysis(meeting_id, params).then((res) => res),
    ...handlerFunctions,
  });
};

export interface AdminAPIPage {
  readonly useGetMeetingSummary: (
    params: any,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  readonly useGetMeetingTranscriptReanalysis: (
    params: any,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  readonly usePostMeetingReanalysis: (
    meeting_id: string,
    handlerFunctions: any
  ) => any;
  readonly usePostAcceptMeetingReanalysis: (
    meeting_id: string,
    handlerFunctions: any
  ) => any;
}
const AdminAPIPage: AdminAPIPage = Object.freeze({
  useGetMeetingSummary,
  useGetMeetingTranscriptReanalysis,
  usePostMeetingReanalysis,
  usePostAcceptMeetingReanalysis
});

export default AdminAPIPage;
